import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAdminApi } from "./useAdminApi";
import ImageLightbox from "./ImageLightbox";
import LaunchBoxIgdbLinkModal from "./LaunchBoxIgdbLinkModal";
import LaunchBoxMetadataModal from "./LaunchBoxMetadataModal";
import CoverCurationModal from "./CoverCurationModal";
import { cover3dLabel } from "./Cover3dPicker";

const PAGE_SIZE = 20;
const LINKED_FILTERS = [
  { key: "", label: "All" },
  { key: "linked", label: "Linked" },
  { key: "unlinked", label: "Unlinked" },
  // Entries a catalog update dropped — kept in the cache on purpose (the app may still
  // hold their id), hidden from search and version lists. See encyclopediaUpdate.ts.
  { key: "removed", label: "Removed" },
];
// launchbox_metadata — see GET /admin/launchbox-links in the API.
const METADATA_FILTERS = [
  { key: "", label: "All" },
  { key: "curated", label: "Curated" },
  { key: "color", label: "Cartridge color" },
  { key: "no_color", label: "No color" },
  { key: "3d_off", label: "3D off" },
];

function Thumb({ src, alt, title, onOpen }) {
  return (
    <img
      className="admin-boxart admin-boxart--row admin-boxart-clickable"
      src={src}
      alt={alt}
      title={title}
      onClick={() => onOpen({ src, alt })}
    />
  );
}

function MetadataSummary({ item }) {
  const savedColor = item.metadata?.cartridge_color;
  return (
    <div className="admin-source-meta admin-metadata-fields">
      Cartridge
      {item.cartridge_color
        ? (
          <>
            <span className="admin-metadata-swatch" style={{ background: item.cartridge_color.hex }} title={item.cartridge_color.key} />
            {item.cartridge_color.name}
          </>
        )
        : savedColor ? <span className="admin-error">not a preset ({savedColor})</span> : <span>default</span>}
      <span>·</span>
      3D
      {item.metadata?.disable_3d
        ? <span className="admin-error">{cover3dLabel(item.metadata.disable_3d)}</span>
        : <span>on</span>}
      {item.curated_face_count > 0 && (
        <>
          <span>·</span>
          <span>{item.curated_face_count === 1 ? "1 own face" : `${item.curated_face_count} own faces`}</span>
        </>
      )}
    </div>
  );
}

// Every LaunchBox game the app has resolved (games_launchbox), newest first, with its
// IGDB link and its hand-curated metadata (launchbox_metadata: cartridge color, 3D).
function LaunchBoxLinksTab({ user, onForbidden }) {
  const apiFetch = useAdminApi(user, onForbidden);
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [nameInput, setNameInput] = useState("");
  const [name, setName] = useState("");
  const [linkedFilter, setLinkedFilter] = useState("");
  const [metadataFilter, setMetadataFilter] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [lightbox, setLightbox] = useState(null);
  const [deletingIds, setDeletingIds] = useState(() => new Set());
  const [linkingItem, setLinkingItem] = useState(null);
  const closeLinkModal = useCallback(() => setLinkingItem(null), []);
  const [editingId, setEditingId] = useState(null); // launchbox_id
  const [curatingId, setCuratingId] = useState(null); // launchbox_id
  const dirtyRef = useRef(false);

  useEffect(() => {
    const handle = setTimeout(() => {
      setName(nameInput.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(handle);
  }, [nameInput]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page: String(page), page_size: String(PAGE_SIZE) });
      if (name) params.set("name", name);
      if (linkedFilter) params.set("linked", linkedFilter);
      if (metadataFilter) params.set("metadata", metadataFilter);
      const data = await apiFetch(`/admin/launchbox-links?${params.toString()}`);
      setItems(data.items);
      setTotalPages(data.total_pages || 1);
      setTotal(data.total || 0);
    } catch (err) {
      if (err.message !== "Forbidden") setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiFetch, page, name, linkedFilter, metadataFilter]);

  useEffect(() => {
    load();
  }, [load]);

  // The list reloads once a modal closes, so a filtered row doesn't vanish mid-edit.
  const closeEditModal = useCallback(() => {
    setEditingId(null);
    setCuratingId(null);
    if (dirtyRef.current) load();
    dirtyRef.current = false;
  }, [load]);
  const markDirty = useCallback(() => {
    dirtyRef.current = true;
  }, []);

  // Resets this item to "never resolved" — deletes the cached games_launchbox doc
  // (and pulls it out of whatever product referenced it) so the next time the app or
  // /games/boxart/v2 asks about this exact launchbox_id, the full cold path runs
  // again instead of serving this cache. For re-testing a resolution by hand. Its
  // launchbox_metadata row is left alone.
  async function deleteEntry(item) {
    const message = item.removed
      ? `"${item.name}" (launchbox_id ${item.launchbox_id}) is no longer in the LaunchBox catalog — this cached copy can't be rebuilt once deleted, and app copies that hold this id will lose their details.\n\nDelete it anyway?`
      : `Delete the cached entry for "${item.name}" (launchbox_id ${item.launchbox_id})? It'll be re-resolved from scratch next time it's requested.`;
    if (!window.confirm(message)) {
      return;
    }
    setDeletingIds((prev) => new Set(prev).add(item.entry_id));
    try {
      await apiFetch(`/admin/launchbox-links/${item.entry_id}`, { method: "DELETE" });
      load();
    } catch (err) {
      if (err.message !== "Forbidden") window.alert("Couldn't delete that entry");
    } finally {
      setDeletingIds((prev) => {
        const next = new Set(prev);
        next.delete(item.entry_id);
        return next;
      });
    }
  }

  function filterButtons(options, value, setValue) {
    return (
      <div className="admin-linked-filter">
        {options.map((option) => (
          <button
            key={option.key || "all"}
            type="button"
            className={`admin-tab admin-linked-filter-btn ${value === option.key ? "admin-tab-active" : ""}`}
            onClick={() => {
              setValue(option.key);
              setPage(1);
            }}
          >
            {option.label}
          </button>
        ))}
      </div>
    );
  }

  // An id the app never resolved has no row here, but its metadata can still be set.
  const uncachedId = items && items.length === 0 && /^\d+$/.test(name) ? Number(name) : null;

  return (
    <div>
      <div className="admin-console-filters">
        <span className="admin-source-meta">IGDB</span>
        {filterButtons(LINKED_FILTERS, linkedFilter, setLinkedFilter)}
        <span className="admin-source-meta">Metadata</span>
        {filterButtons(METADATA_FILTERS, metadataFilter, setMetadataFilter)}
      </div>
      <div className="admin-links-search">
        <input
          type="text"
          placeholder="Search by LaunchBox name or launchbox_id…"
          value={nameInput}
          onChange={(e) => setNameInput(e.target.value)}
          className="admin-tgdb-link-input admin-links-search-input"
        />
      </div>

      {loading && <p className="admin-status">Loading…</p>}
      {error && <p className="admin-status admin-error">{error}</p>}

      <div className="admin-table glass">
        {items && items.length === 0 && (
          <p className="admin-status">
            No LaunchBox items found.
            {uncachedId !== null && (
              <>
                {" "}
                <button type="button" className="admin-signout-btn" onClick={() => setEditingId(uncachedId)}>
                  Edit metadata for launchbox_id {uncachedId}
                </button>
              </>
            )}
          </p>
        )}
        {items && items.map((item) => (
          <div className="admin-row admin-row--covers" key={item.entry_id}>
            <div className="admin-boxart-sides admin-boxart-sides--row admin-boxart-sides--row3">
              {item.cover_url
                ? <Thumb src={item.cover_url} alt={`${item.name} front`} title="Front" onOpen={setLightbox} />
                : <div className="admin-boxart-placeholder admin-boxart-placeholder--row">No art</div>}
              <div className="admin-boxart-back-slot">
                {item.back_cover_url && <Thumb src={item.back_cover_url} alt={`${item.name} back`} title="Back cover" onOpen={setLightbox} />}
              </div>
              <div className="admin-boxart-back-slot">
                {item.label_url && <Thumb src={item.label_url} alt={`${item.name} cartridge`} title="Cartridge" onOpen={setLightbox} />}
              </div>
            </div>
            <div className="admin-row-main">
              <div className="admin-source-name">{item.name}</div>
              {item.removed && (
                <div className="admin-launchbox-removed">
                  <span className="admin-queue-status admin-db-status--removed">
                    Removed from catalog{item.removed.version != null && ` in v${item.removed.version}`}
                  </span>
                  <span className="admin-row-id">flagged {new Date(item.removed.at).toLocaleDateString()} · hidden from search and version lists</span>
                </div>
              )}
              <div className="admin-source-meta">
                {item.platform_name || `platform_id ${item.platform_id}`} · {item.country_name || "Unknown country"} · launchbox_id {item.launchbox_id}
                {item.tgdb_id != null && ` · tgdb_id ${item.tgdb_id}`}
                {item.metacritic_id != null && ` · metacritic_id ${item.metacritic_id}`}
              </div>
              {item.search_name !== item.name && (
                <div className="admin-source-meta">searched as "{item.search_name}"</div>
              )}
              {item.game_product_id
                ? (
                  <div className="admin-status--inline admin-covers-linked">
                    {item.igdb ? `Linked to ${item.igdb.name}` : `Linked to product ${item.game_product_id} (no IGDB yet)`}
                  </div>
                )
                : <div className="admin-status--inline admin-covers-linked">Not linked</div>}
              <MetadataSummary item={item} />
            </div>
            <div className="admin-row-actions admin-row-actions--gap">
              {/* Unlinked only — a linked entry is moved by unlinking it on its card
                  first (Links tab); the backend 409s otherwise anyway. */}
              {!item.game_product_id && (
                <button
                  type="button"
                  className="admin-signout-btn"
                  onClick={() => setLinkingItem(item)}
                  title="Search IGDB and link this entry to a game"
                >
                  Link to IGDB
                </button>
              )}
              <button
                type="button"
                className="admin-signout-btn"
                onClick={() => setEditingId(item.launchbox_id)}
                title="Cartridge color and 3D box for this version"
              >
                Metadata
              </button>
              <button
                type="button"
                className="admin-signout-btn"
                onClick={() => setCuratingId(item.launchbox_id)}
                title="Crop or upload this version's front, back and spine"
              >
                Cover
              </button>
              <button
                type="button"
                className="admin-signout-btn admin-delete-btn"
                onClick={() => deleteEntry(item)}
                disabled={deletingIds.has(item.entry_id)}
                title="Delete cached entry — forces a fresh resolve next time"
              >
                {deletingIds.has(item.entry_id) ? "…" : "Delete"}
              </button>
            </div>
          </div>
        ))}
      </div>

      {items && items.length > 0 && (
        <div className="admin-pagination">
          <button
            type="button"
            className="admin-signout-btn"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1 || loading}
          >
            Previous
          </button>
          <span className="admin-pagination-status">
            Page {page} of {totalPages} · {total} total
          </span>
          <button
            type="button"
            className="admin-signout-btn"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages || loading}
          >
            Next
          </button>
        </div>
      )}

      <ImageLightbox src={lightbox?.src} alt={lightbox?.alt} onClose={() => setLightbox(null)} />
      {linkingItem && (
        <LaunchBoxIgdbLinkModal item={linkingItem} apiFetch={apiFetch} onLinked={load} onClose={closeLinkModal} />
      )}
      {editingId !== null && (
        <LaunchBoxMetadataModal launchboxId={editingId} apiFetch={apiFetch} onChanged={markDirty} onClose={closeEditModal} />
      )}
      {curatingId !== null && (
        <CoverCurationModal launchboxId={curatingId} apiFetch={apiFetch} onChanged={markDirty} onClose={closeEditModal} />
      )}
    </div>
  );
}

export default LaunchBoxLinksTab;
